import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/layout/Layout';
import { ExternalLink, Calendar, Users, MapPin } from 'lucide-react';
import { useContactData } from '@/hooks/useContactData';
import { useNavigationData } from '@/hooks/useNavigationData';
import { useHomepageData } from '@/hooks/useHomepageData';
import { useSiteData } from '@/hooks/useSiteData';

export function MoreInfo() {
  const { siteData } = useSiteData();
  const { contactInfo } = useContactData();
  const { navigationItems } = useNavigationData();
  const { pageData } = useHomepageData();

  const additionalPages = [
    {
      title: "Club History",
      description: "Learn about our founding in 1930 and rich heritage",
      href: "/history",
      icon: Users
    },
    {
      title: "Meeting Schedule",
      description: "Regular meeting times and special gatherings",
      href: "/events",
      icon: Calendar
    },
    {
      title: "Location & Directions",
      description: "Detailed directions to our facilities",
      href: "/location",
      icon: MapPin
    },
    {
      title: "Civilian Marksmanship Program",
      description: "Information about CMP participation and events",
      href: "/cmp",
      icon: ExternalLink,
      isNew: true
    }
  ];

  return (
    <>
      <Helmet>
        <title>More Information - {siteData.title}</title>
        <meta name="description" content="Additional resources and information about Wayland Rod and Gun Club including history, meetings, events, and helpful links." />
        <meta name="keywords" content="club history, meetings, events, links, resources, Wayland Rod Gun Club" />
      </Helmet>

      <Layout contactInfo={contactInfo} navigationItems={navigationItems}>
        <div className="p-4 sm:p-8 space-y-8 sm:space-y-12 max-w-6xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-cursive text-primary-700 mb-4">
              Additional Information
            </h1>
            <p className="text-lg text-foreground/80 max-w-2xl mx-auto leading-relaxed">
              Find more details about our club history, meetings, events, and helpful resources.
            </p>
          </div>
          
          {/* Quick Links Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {additionalPages.map((page, index) => {
              const IconComponent = page.icon;
              return (
                <a
                  key={index}
                  href={page.href}
                  className="card-professional hover:shadow-elegant transition-all duration-200 hover:-translate-y-1 block"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-foreground">
                          {page.title}
                        </h3>
                        {page.isNew && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-accent text-accent-foreground rounded-full text-xs font-medium">
                            NEW
                          </span>
                        )}
                      </div>
                      <p className="text-foreground/70 text-sm leading-relaxed">
                        {page.description}
                      </p>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>

          {/* External Links Section */}
          {pageData.links && pageData.links.length > 0 && (
            <div className="space-y-8">
              <div className="text-center">
                <h2 className="text-2xl font-display font-semibold text-secondary mb-4">
                  Helpful Resources
                </h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                {pageData.links.map((linkSection, index) => (
                  <div key={index} className="card-professional">
                    <h3 className="text-xl font-display font-semibold text-secondary mb-4">
                      {linkSection.title}
                    </h3>
                    <div className="space-y-3">
                      {linkSection.items.map((item, idx) => (
                        <a
                          key={idx}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-start gap-3 p-3 bg-muted/20 rounded-lg hover:bg-muted/40 transition-colors group"
                        >
                          <ExternalLink className="w-4 h-4 text-primary mt-0.5 flex-shrink-0 group-hover:text-primary/70" />
                          <span className="text-foreground/80 group-hover:text-foreground font-medium text-sm leading-relaxed">
                            {item.text}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Layout>
    </>
  );
}